import React, { useEffect, useState, useCallback } from 'react';
import {
  getAllApplications,
  updateApplication,
  assignModeratorToApplication,
  getRandomActiveModerator,
  createClient,
} from '../../database/firebase';
import { useUserRole } from '../../context/UserRoleContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiPencil,
  HiTrash,
  HiXMark,
  HiUser,
  HiEnvelope,
  HiCalendarDays,
  HiCalendar,
  HiClock,
  HiDocumentText,
  HiBuildingOffice2,
  HiExclamationTriangle,
  HiCheckCircle,
  HiInformationCircle,
  HiCurrencyDollar,
  HiLightBulb,
  HiPlus,
  HiBars3,
  HiKey,
  HiUserPlus,
  HiDocumentArrowDown,
  HiPhoto,
  HiPlay,
  HiArrowDownTray,
  HiMagnifyingGlass,
} from 'react-icons/hi2';
import { firestore } from '../../database/firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  {
    value: 'in progress',
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    value: 'completed',
    label: 'Completed',
    color: 'bg-green-100 text-green-700 border-green-200',
  },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200' },
];

const getStatusStyle = status => {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.color : 'bg-gray-100 text-gray-600 border-gray-200';
};

const BIFStatusOptions = [
  {
    value: 'pending',
    label: 'Pending Review',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  {
    value: 'under_review',
    label: 'Under Review',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    value: 'approved',
    label: 'Approved',
    color: 'bg-green-100 text-green-700 border-green-200',
  },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'funded', label: 'Funded', color: 'bg-purple-100 text-purple-700 border-purple-200' },
];

const getBIFStatusStyle = status => {
  const option = BIFStatusOptions.find(opt => opt.value === status);
  return option ? option.color : 'bg-gray-100 text-gray-600 border-gray-200';
};

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-green-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    {type === 'info' && <HiInformationCircle className="h-5 w-5 text-blue-600" />}
    <span
      className={`text-sm font-medium ${
        type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'
      }`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${
        type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'
      }`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const ApplicationsTable = () => {
  const { userRole } = useUserRole();
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [bifApplications, setBIFApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [error, setError] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [showHamburgerMenu, setShowHamburgerMenu] = useState({});
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    application: null,
  });
  const [rejectConfirm, setRejectConfirm] = useState({
    isOpen: false,
    application: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const canManageApplications = [
    'admin',
    'coach',
    'moderator',
    'Business Advisor',
    'superAdmin',
  ].includes(userRole);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchBIFApplications = useCallback(async () => {
    setLoading(true);
    try {
      const bifQuery = query(collection(firestore, 'bif_applications'));
      const querySnapshot = await getDocs(bifQuery);
      const applications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBIFApplications(applications);
      if (applications.length === 0) {
        showToast('No BIF applications found', 'info');
      }
    } catch (err) {
      setError('Failed to fetch BIF applications');
      showToast('Failed to load BIF applications', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApplicationsWithClients = useCallback(async () => {
    setLoading(true);
    try {
      const apps = await getAllApplications();
      const merged = apps.map(app => ({
        ...app,
        firstname: app.firstname || '',
        lastname: app.lastname || '',
        email: app.email || '',
        phone: app.phone || '',
        businessStage: app.businessStage || '',
        program: app.program || '',
        location: app.location || '',
        organization: app.organization || '',
      }));
      setApplications(merged);
      if (merged.length === 0) {
        showToast('No applications found', 'info');
      }
    } catch (err) {
      setError('Failed to fetch applications');
      showToast('Failed to load applications', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplicationsWithClients();
    } else {
      fetchBIFApplications();
    }
  }, [activeTab, fetchApplicationsWithClients, fetchBIFApplications]);

  const handleView = app => {
    setSelectedApplication(app);
    setShowDetailsModal(true);
  };

  const handleEdit = app => {
    setSelectedApp(app);
    setEditStatus(app.status || 'pending');
    setEditNotes(app.notes || '');
  };

  const handleDeleteClick = app => {
    setDeleteConfirm({
      isOpen: true,
      application: app,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.application) return;

    setIsDeleting(true);
    try {
      if (activeTab === 'bif') {
        await deleteDoc(doc(firestore, 'bif_applications', deleteConfirm.application.id));
        setBIFApplications(prev => prev.filter(a => a.id !== deleteConfirm.application.id));
      } else {
        await deleteDoc(doc(firestore, 'applications', deleteConfirm.application.id));
        setApplications(prev => prev.filter(a => a.id !== deleteConfirm.application.id));
      }
      showToast('Application deleted successfully', 'success');
      setDeleteConfirm({ isOpen: false, application: null });

      if (activeTab === 'applications') {
        fetchApplicationsWithClients();
      } else if (activeTab === 'bif') {
        fetchBIFApplications();
      }
    } catch (error) {
      showToast(`Failed to delete application: ${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, application: null });
  };

  const handleApproveApplication = async app => {
    setApprovalData(app);
    setNewPassword('');
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }

    if (!approvalData.email && !approvalData.userEmail) {
      showToast('Email address is required for account creation', 'error');
      return;
    }

    const firstName = approvalData.firstname || '';
    const lastName = approvalData.lastname || '';

    if (!firstName || !lastName) {
      showToast(
        'Applicant name is required for account creation. Please ensure the application has first and last name.',
        'error'
      );
      return;
    }

    setSaving(true);
    try {
      const email = approvalData.email || approvalData.userEmail;

      // Create the new user account using a temporary Firebase app (preserves current session)
      const tempApp = initializeApp(
        {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        },
        'temp-app-' + Date.now()
      );

      const tempAuth = getAuth(tempApp);
      const userCredential = await createUserWithEmailAndPassword(tempAuth, email, newPassword);
      const uid = userCredential.user.uid;

      // Prepare client data
      const clientData = {
        userId: uid,
        applicantId: approvalData.applicantId || approvalData.id,
        firstname: firstName,
        lastname: lastName,
        email: email,
        phone: approvalData.phone || '',
        businessStage: approvalData.businessStage || '',
        program: approvalData.program || '',
        location: approvalData.location || '',
        organization: approvalData.organization || '',
        business: approvalData.business || {},
        documents: approvalData.documents || {},
        interview: approvalData.interview || {},
        assigned: approvalData.assigned || '',
        assignedEmail: approvalData.assignedEmail || '',
        assignedCoachName: approvalData.assignedCoachName || '',
        assignedCoachEmail: approvalData.assignedCoachEmail || '',
        role: 'Client',
        status: 'active',
        approvedAt: new Date().toISOString(),
        createdAt: approvalData.createdAt || new Date().toISOString(),
        approvedBy: 'admin', // This should be the actual admin user
      };

      // Perform all operations in parallel
      await Promise.all([
        createClient(clientData, uid),
        deleteDoc(doc(firestore, 'applications', approvalData.id)),
      ]);

      // Sign out from the temporary auth to clean up (does not affect current session)
      await tempAuth.signOut();

      // Update local state
      setApplications(prev => prev.filter(app => app.id !== approvalData.id));

      // Close modal and show success message BEFORE any redirect happens
      setShowApprovalModal(false);
      showToast(`Account created successfully for ${firstName} ${lastName}!`, 'success');

      // Refresh the applications list
      if (activeTab === 'applications') {
        fetchApplicationsWithClients();
      }
    } catch (error) {
      let errorMessage = 'Failed to approve application';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email already exists. Please use a different email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectClick = app => {
    setRejectConfirm({
      isOpen: true,
      application: app,
    });
  };

  const handleRejectConfirm = async () => {
    if (!rejectConfirm.application) return;

    setIsRejecting(true);
    try {
      // Get the full application data
      const appDoc = await getDoc(doc(firestore, 'applications', rejectConfirm.application.id));
      const appData = appDoc.data();

      // Add rejection metadata
      const rejectedAppData = {
        ...appData,
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectedBy: 'admin',
        updatedAt: new Date().toISOString(),
      };

      // Move to rejected_applications collection
      await setDoc(
        doc(firestore, 'rejected_applications', rejectConfirm.application.id),
        rejectedAppData
      );

      // Delete from applications collection
      await deleteDoc(doc(firestore, 'applications', rejectConfirm.application.id));

      setApplications(prev =>
        prev.filter(application => application.id !== rejectConfirm.application.id)
      );

      showToast('Application rejected and moved to rejected applications', 'success');
      setRejectConfirm({ isOpen: false, application: null });

      if (activeTab === 'applications') {
        fetchApplicationsWithClients();
      }
    } catch (error) {
      showToast(`Failed to reject application: ${error.message}`, 'error');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRejectCancel = () => {
    setRejectConfirm({ isOpen: false, application: null });
  };

  const toggleHamburgerMenu = appId => {
    setShowHamburgerMenu(prev => {
      const newState = {};
      if (!prev[appId]) {
        newState[appId] = true;
      }
      return newState;
    });
  };

  const closeAllHamburgerMenus = () => {
    setShowHamburgerMenu({});
  };

  const handleDocumentPreview = document => {
    setPreviewDocument(document);
    setShowDocumentPreview(true);
  };

  const getFileIcon = fileType => {
    if (fileType?.includes('image')) return <HiPhoto className="h-4 w-4" />;
    if (fileType?.includes('video')) return <HiPlay className="h-4 w-4" />;
    if (fileType?.includes('pdf')) return <HiDocumentText className="h-4 w-4" />;
    return <HiDocumentArrowDown className="h-4 w-4" />;
  };

  const filteredApplications = applications.filter(
    app =>
      searchTerm === '' ||
      app.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.businessStage?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.hamburger-menu-container') &&
        !event.target.closest('.fixed.inset-0.z-40')
      ) {
        closeAllHamburgerMenus();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!canManageApplications) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <HiExclamationTriangle className="mx-auto mb-4 h-16 w-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to access the applications management system.
          </p>
        </div>
      </div>
    );
  }

  const renderBIFTable = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50/80">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <div className="flex items-center gap-2">
                  <HiBuildingOffice2 className="h-4 w-4" />
                  Company
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <div className="flex items-center gap-2">
                  <HiLightBulb className="h-4 w-4" />
                  Project
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <div className="flex items-center gap-2">
                  <HiCurrencyDollar className="h-4 w-4" />
                  Amount
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <div className="flex items-center gap-2">
                  <HiClock className="h-4 w-4" />
                  Status
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <div className="flex items-center gap-2">
                  <HiCalendarDays className="h-4 w-4" />
                  Submitted
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {bifApplications.map((app, index) => (
              <tr key={app.id} className="group transition-colors hover:bg-blue-50/30">
                <motion.td
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="px-6 py-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-sm font-semibold text-white shadow-sm">
                      {app.companyName?.[0]?.toUpperCase() || 'C'}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{app.companyName}</div>
                      <div className="text-sm text-gray-500">{app.contactPerson}</div>
                    </div>
                  </div>
                </motion.td>
                <motion.td
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.02 }}
                  className="px-6 py-4"
                >
                  <div className="max-w-xs">
                    <div className="font-medium text-gray-900">{app.projectTitle}</div>
                    <div className="line-clamp-2 text-sm text-gray-500">
                      {app.projectDescription}
                    </div>
                  </div>
                </motion.td>
                <motion.td
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.04 }}
                  className="px-6 py-4"
                >
                  <div className="text-sm font-medium text-gray-900">
                    BWP {Number(app.requestedAmount).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    Total: BWP {Number(app.totalBudget).toLocaleString()}
                  </div>
                </motion.td>
                <motion.td
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.06 }}
                  className="px-6 py-4"
                >
                  <span
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getBIFStatusStyle(app.status)}`}
                  >
                    {app.status || 'pending'}
                  </span>
                </motion.td>
                <motion.td
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.08 }}
                  className="px-6 py-4 text-sm text-gray-500"
                >
                  {new Date(app.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </motion.td>
                <motion.td
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.1 }}
                  className="px-6 py-4"
                >
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => handleView(app)}
                      className="rounded-lg p-2 text-gray-500 opacity-70 transition-all duration-200 hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100"
                      title="View Details"
                    >
                      <HiEye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(app)}
                      className="rounded-lg p-2 text-gray-500 opacity-70 transition-all duration-200 hover:bg-amber-50 hover:text-amber-600 group-hover:opacity-100"
                      title="Edit Application"
                    >
                      <HiPencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(app)}
                      className="rounded-lg p-2 text-gray-500 opacity-70 transition-all duration-200 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                      title="Delete Application"
                    >
                      <HiTrash className="h-4 w-4" />
                    </button>
                  </div>
                </motion.td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );

  const stats = [
    {
      label: 'Total Applications',
      value: applications.length,
      icon: <HiDocumentText className="h-6 w-6 text-blue-600" />,
      bg: 'bg-blue-100',
    },
    {
      label: 'Pending',
      value: applications.filter(a => a.status === 'pending').length,
      icon: <HiClock className="h-6 w-6 text-orange-500" />,
      bg: 'bg-orange-100',
    },
    {
      label: 'Completed',
      value: applications.filter(a => a.status === 'completed').length,
      icon: <HiCheckCircle className="h-6 w-6 text-green-600" />,
      bg: 'bg-green-100',
    },
    {
      label: 'Rejected',
      value: applications.filter(a => a.status === 'rejected').length,
      icon: <HiXMark className="h-6 w-6 text-red-600" />,
      bg: 'bg-red-100',
    },
    {
      label: 'Ready for Approval',
      value: applications.filter(a => a.status === 'completed' || a.status === 'pending').length,
      icon: <HiUserPlus className="h-6 w-6 text-purple-600" />,
      bg: 'bg-purple-100',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
                Applications Management
              </h1>
              <p className="mt-2 text-sm text-gray-600 md:text-base">
                Review and manage all submitted applications with document preview
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <HiMagnifyingGlass className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab('applications')}
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-white shadow-lg transition-all hover:shadow-xl"
              >
                <HiPlus className="h-5 w-5" />
                New Application
              </motion.button>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-3 md:space-x-8">
              <button
                onClick={() => setActiveTab('applications')}
                className={`inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === 'applications'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <HiDocumentText className="h-5 w-5" />
                Regular Applications
              </button>

              <button
                onClick={() => setActiveTab('client_relations')}
                className={`inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === 'client_relations'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <HiUser className="h-5 w-5" />
                Client Relations
              </button>
            </nav>
          </div>
        </div>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-center gap-2">
                <HiExclamationTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          ) : activeTab === 'applications' ? (
            applications.length === 0 ? (
              <div className="p-8 text-center">
                <HiDocumentText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-black">No applications found</h3>
                <p className="mt-1 text-gray-600">
                  Applications will appear here once they are submitted.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <div className="flex items-center gap-2">
                          <HiUser className="h-4 w-4" /> Applicant
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <div className="flex items-center gap-2">
                          <HiEnvelope className="h-4 w-4" /> Email
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <div className="flex items-center gap-2">
                          <HiBuildingOffice2 className="h-4 w-4" /> Business
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <div className="flex items-center gap-2">
                          <HiClock className="h-4 w-4" /> Status
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <div className="flex items-center gap-2">
                          <HiCalendarDays className="h-4 w-4" /> Submitted
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <div className="flex items-center gap-2">
                          <HiDocumentText className="h-4 w-4" /> Documents
                        </div>
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredApplications.map((app, index) => (
                      <motion.tr
                        key={app.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                              {(app.firstname ||
                                app.applicantName ||
                                app.userEmail ||
                                app.id)?.[0]?.toUpperCase() || 'A'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-black">
                                {app.firstname || ''} {app.lastname || ''}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {app.applicantId || app.id?.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm text-black">{app.email || app.userEmail}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {app.business?.businessName || 'N/A'}
                            </div>
                            <div className="text-gray-500">
                              {app.business?.industry || 'N/A'} •{' '}
                              {app.business?.yearsInOperation || '0'} years
                            </div>
                            <div className="text-xs text-gray-400">
                              Revenue:{' '}
                              {app.business?.annualRevenue
                                ? `$${parseInt(app.business.annualRevenue).toLocaleString()}`
                                : 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getStatusStyle(app.status || 'pending')}`}
                          >
                            {app.status || 'pending'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {app.createdAt?.toDate
                            ? app.createdAt.toDate().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : app.createdAt || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {app.documents ? (
                              Object.entries(app.documents).map(([docType, doc], docIndex) => (
                                <motion.button
                                  key={docIndex}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() =>
                                    handleDocumentPreview({
                                      ...doc,
                                      type: doc.name?.includes('.pdf')
                                        ? 'application/pdf'
                                        : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                          ? 'image/jpeg'
                                          : 'application/octet-stream',
                                    })
                                  }
                                  className="flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-100"
                                  title={`Preview ${doc.name || docType}`}
                                >
                                  {getFileIcon(
                                    doc.name?.includes('.pdf')
                                      ? 'application/pdf'
                                      : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                        ? 'image/jpeg'
                                        : 'application/octet-stream'
                                  )}
                                  <span className="max-w-20 truncate">
                                    {docType.replace(/([A-Z])/g, ' $1').trim()}
                                  </span>
                                </motion.button>
                              ))
                            ) : (
                              <span className="text-sm text-gray-400">No documents</span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                          <div className="hamburger-menu-container relative">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => toggleHamburgerMenu(app.id)}
                              className="rounded-lg bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200"
                              title="Actions"
                            >
                              <HiBars3 className="h-4 w-4" />
                            </motion.button>

                            {showHamburgerMenu[app.id] && (
                              <>
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  onClick={() =>
                                    setShowHamburgerMenu(prev => ({ ...prev, [app.id]: false }))
                                  }
                                  className="fixed inset-0 z-40 bg-black/20"
                                />

                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                  className="fixed z-50 w-48 rounded-lg border border-gray-200 bg-white shadow-xl"
                                  style={{
                                    position: 'fixed',
                                    top: '100px',
                                    right: '20px',
                                    zIndex: 9999,
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                  }}
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleView(app);
                                        setShowHamburgerMenu(prev => ({
                                          ...prev,
                                          [app.id]: false,
                                        }));
                                      }}
                                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                      <HiEye className="h-4 w-4" />
                                      View Details
                                    </button>

                                    {app.status === 'pending' && (
                                      <>
                                        <button
                                          onClick={() => {
                                            handleApproveApplication(app);
                                            setShowHamburgerMenu(prev => ({
                                              ...prev,
                                              [app.id]: false,
                                            }));
                                          }}
                                          className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50"
                                        >
                                          <HiCheckCircle className="h-4 w-4" />
                                          Approve & Create Account
                                        </button>

                                        <button
                                          onClick={() => {
                                            handleRejectClick(app);
                                            setShowHamburgerMenu(prev => ({
                                              ...prev,
                                              [app.id]: false,
                                            }));
                                          }}
                                          className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                                        >
                                          <HiXMark className="h-4 w-4" />
                                          Reject Application
                                        </button>
                                      </>
                                    )}

                                    {app.status === 'completed' && (
                                      <button
                                        onClick={() => {
                                          handleApproveApplication(app);
                                          setShowHamburgerMenu(prev => ({
                                            ...prev,
                                            [app.id]: false,
                                          }));
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50"
                                      >
                                        <HiUserPlus className="h-4 w-4" />
                                        Create Account
                                      </button>
                                    )}

                                    <button
                                      onClick={() => {
                                        handleEdit(app);
                                        setShowHamburgerMenu(prev => ({
                                          ...prev,
                                          [app.id]: false,
                                        }));
                                      }}
                                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-orange-700 hover:bg-orange-50"
                                    >
                                      <HiPencil className="h-4 w-4" />
                                      Edit Application
                                    </button>

                                    <button
                                      onClick={() => {
                                        handleDeleteClick(app);
                                        setShowHamburgerMenu(prev => ({
                                          ...prev,
                                          [app.id]: false,
                                        }));
                                      }}
                                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                                    >
                                      <HiTrash className="h-4 w-4" />
                                      Delete Application
                                    </button>
                                  </div>
                                </motion.div>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'bif' ? (
            bifApplications.length === 0 ? (
              <div className="p-8 text-center">
                <HiCurrencyDollar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-black">No BIF applications found</h3>
                <p className="mt-1 text-gray-600">
                  BIF applications will appear here once they are submitted.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">{renderBIFTable()}</div>
            )
          ) : (
            <div className="p-8 text-center">
              <HiUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-black">Client Relations</h3>
              <p className="mt-1 text-gray-600">
                This section will show client relations and engagement data.
              </p>
            </div>
          )}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {stats.map((stat, idx) => (
            <div
              key={stat.label}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-black">{stat.value}</p>
                </div>
                <div className={`rounded-full p-3 ${stat.bg}`}>{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>
        <AnimatePresence>
          {showApprovalModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              >
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <HiUserPlus className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Approve Application</h3>
                      <p className="text-sm text-gray-600">
                        Create account for {approvalData.firstname} {approvalData.lastname}
                      </p>
                      <p className="text-xs text-gray-500">
                        Business: {approvalData.business?.businessName || 'N/A'} •{' '}
                        {approvalData.business?.industry || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-lg bg-blue-50 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <HiKey className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Set Account Password</span>
                    </div>
                    <p className="mb-4 text-sm text-blue-700">
                      This password will be used by the user to login to their account.
                    </p>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <HiKey className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter secure password (min 6 characters)"
                        minLength={6}
                      />
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Password requirements: Minimum 6 characters
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowApprovalModal(false)}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-700 transition-colors hover:bg-gray-50"
                    disabled={saving}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleApprovalSubmit}
                    disabled={saving || !newPassword || newPassword.length < 6}
                    className="flex-1 rounded-lg bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 text-white shadow-lg transition-all hover:shadow-xl disabled:opacity-50 disabled:hover:scale-100"
                  >
                    <div className="flex items-center justify-center gap-2">
                      {saving ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          <span>Creating Account...</span>
                        </>
                      ) : (
                        <>
                          <HiCheckCircle className="h-5 w-5" />
                          <span>Approve & Create Account</span>
                        </>
                      )}
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showDocumentPreview && previewDocument && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    {getFileIcon(previewDocument.type)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {previewDocument.name || 'Document Preview'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {previewDocument.type || 'Unknown file type'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => window.open(previewDocument.url, '_blank')}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      <HiArrowDownTray className="h-4 w-4" />
                      Download
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowDocumentPreview(false)}
                      className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
                    >
                      <HiXMark className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>
                <div className="p-6">
                  {previewDocument.type?.includes('image') ? (
                    <img
                      src={previewDocument.url}
                      alt={previewDocument.name}
                      className="mx-auto max-h-96 rounded-lg object-contain"
                    />
                  ) : previewDocument.type?.includes('pdf') ? (
                    <iframe
                      src={previewDocument.url}
                      className="h-96 w-full rounded-lg border"
                      title={previewDocument.name}
                    />
                  ) : (
                    <div className="flex h-96 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                      <div className="text-center">
                        <HiDocumentText className="mx-auto h-16 w-16 text-gray-400" />
                        <p className="mt-2 text-gray-600">
                          Preview not available for this file type
                        </p>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => window.open(previewDocument.url, '_blank')}
                          className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                        >
                          <HiArrowDownTray className="h-4 w-4" />
                          Download to view
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showDetailsModal && selectedApplication && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                      <HiUser className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Application Details</h3>
                      <p className="text-sm text-gray-600">
                        {selectedApplication.firstname} {selectedApplication.lastname}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowDetailsModal(false)}
                    className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
                  >
                    <HiXMark className="h-5 w-5" />
                  </motion.button>
                </div>

                <div className="space-y-6 p-6">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <HiUser className="h-5 w-5" />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Full Name</label>
                        <p className="text-gray-900">
                          {selectedApplication.firstname} {selectedApplication.lastname}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Email</label>
                        <p className="text-gray-900">
                          {selectedApplication.email || selectedApplication.userEmail}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Phone</label>
                        <p className="text-gray-900">{selectedApplication.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Application ID</label>
                        <p className="text-gray-900">
                          {selectedApplication.applicantId || selectedApplication.id}
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedApplication.business && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <HiBuildingOffice2 className="h-5 w-5" />
                        Business Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Business Name</label>
                          <p className="text-gray-900">
                            {selectedApplication.business.businessName || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Industry</label>
                          <p className="text-gray-900">
                            {selectedApplication.business.industry || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Registration Number
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.business.registrationNumber || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Years in Operation
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.business.yearsInOperation || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Annual Revenue
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.business.annualRevenue
                              ? `$${parseInt(selectedApplication.business.annualRevenue).toLocaleString()}`
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Employee Count
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.business.employeeCount || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedApplication.interview && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <HiCalendar className="h-5 w-5" />
                        Interview Preferences
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Preferred Date
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.interview.preferredDate || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Preferred Time
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.interview.preferredTime || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Interview Type
                          </label>
                          <p className="capitalize text-gray-900">
                            {selectedApplication.interview.interviewType || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            Additional Notes
                          </label>
                          <p className="text-gray-900">
                            {selectedApplication.interview.additionalNotes || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedApplication.documents && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <HiDocumentText className="h-5 w-5" />
                        Documents
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {Object.entries(selectedApplication.documents).map(([docType, doc]) => (
                          <div
                            key={docType}
                            className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                          >
                            <div className="flex items-center gap-3">
                              {getFileIcon(
                                doc.name?.includes('.pdf')
                                  ? 'application/pdf'
                                  : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                    ? 'image/jpeg'
                                    : 'application/octet-stream'
                              )}
                              <div>
                                <p className="font-medium text-gray-900">
                                  {docType.replace(/([A-Z])/g, ' $1').trim()}
                                </p>
                                <p className="text-sm text-gray-500">{doc.name}</p>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                handleDocumentPreview({
                                  ...doc,
                                  type: doc.name?.includes('.pdf')
                                    ? 'application/pdf'
                                    : doc.name?.includes('.jpg') || doc.name?.includes('.png')
                                      ? 'image/jpeg'
                                      : 'application/octet-stream',
                                })
                              }
                              className="rounded-lg bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                            >
                              View
                            </motion.button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Application Status */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <HiClock className="h-5 w-5" />
                      Application Status
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Status</label>
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getStatusStyle(selectedApplication.status || 'pending')}`}
                        >
                          {selectedApplication.status || 'pending'}
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Created At</label>
                        <p className="text-gray-900">
                          {selectedApplication.createdAt?.toDate
                            ? selectedApplication.createdAt.toDate().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : selectedApplication.createdAt || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Updated At</label>
                        <p className="text-gray-900">
                          {selectedApplication.updatedAt?.toDate
                            ? selectedApplication.updatedAt.toDate().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : selectedApplication.updatedAt || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Application"
          message="Are you sure you want to delete this application? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />

        <ConfirmationModal
          isOpen={rejectConfirm.isOpen}
          onClose={handleRejectCancel}
          onConfirm={handleRejectConfirm}
          title="Reject Application"
          message="Are you sure you want to reject this application? It will be moved to rejected applications."
          confirmText="Reject"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isRejecting}
        />
      </div>
    </div>
  );
};

export default ApplicationsTable;
